/**
 * Resolve WBS/CBS UUID to "CODE — Name" using project structure context (cached).
 */
import React, { useMemo } from 'react';
import { Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { projectService } from '@/services/project.service';

const { Text } = Typography;

export type BudgetCodeLabelProps = {
  projectId?: string | null;
  nodeId?: string | null;
  kind?: 'wbs' | 'cbs';
  fallback?: string;
};

function formatNode(n: { code?: string; title?: string; name?: string } | undefined): string {
  if (!n) return '';
  const code = String(n.code || '').trim();
  const name = String(n.title || n.name || '').trim();
  if (code && name && name.toLowerCase() !== code.toLowerCase()) return `${code} — ${name}`;
  return code || name || '';
}

export const BudgetCodeLabel: React.FC<BudgetCodeLabelProps> = ({
  projectId,
  nodeId,
  kind = 'wbs',
  fallback = '—',
}) => {
  const structureQ = useQuery({
    queryKey: ['project-budget-structure', projectId],
    queryFn: () => projectService.getBudgetStructureContext(String(projectId)),
    enabled: !!projectId && !!nodeId,
    staleTime: 60_000,
  });

  const label = useMemo(() => {
    if (!nodeId) return fallback;
    const data = structureQ.data as Record<string, unknown> | undefined;
    if (!data) return fallback;
    const list =
      kind === 'cbs'
        ? ((data.cbs_codes || []) as Record<string, unknown>[])
        : ((data.nodes || []) as Record<string, unknown>[]);
    const n = list.find((x) => String(x.id) === String(nodeId));
    return formatNode(n as any) || fallback;
  }, [structureQ.data, nodeId, kind, fallback]);

  if (!nodeId) return <Text type="secondary">{fallback}</Text>;
  return (
    <Text ellipsis={{ tooltip: label }} style={{ maxWidth: 160 }}>
      {label}
    </Text>
  );
};

export default BudgetCodeLabel;
